<template>
  <Modal
    ref="createTrainerModal"
    :title="modalTitle"
    @closed="resetData"
  >
    <!-- <template #modal-header /> -->
    <template #modal-main>
      <div class="settings scrollbar">
        <div class="container">
          <div class="static-card">
            <div class="d-flex align-items-center justify-content-between gap-2">
              <h2>Description</h2>
              <HelpTip>
                A training session enables you to train a model based on the selected model type.
                The duration of the session can range from a few minutes to several days,
                depending on the size of the dataset and the number of training epochs.
              </HelpTip>
            </div>

            <div class="row mt-3">
              <div class="col-12 settings__control-group">
                <label class="required">Trainer Type</label>
                <select v-model="schemaType" class="select" required>
                  <option :value="''" disabled>Select a Trainer Type</option>
                  <template v-for="schema in schemaList" :key="schema.name">
                    <option :value="schema.name">{{ schema.label }}</option>
                  </template>
                </select>
              </div>
            </div>

            <div class="mt-4 mb-2">
              <div class="d-flex align-items-center justify-content-between gap-2">
                <h2 class="mb-0">Select Manage Type</h2>
                <HelpTip>
                  By default, a training session runs on a cloud server instance equipped with GPU resources.
                  However, you have the option to enable a "User Managed Trainer," allowing you to run a custom training script.
                  Edgefirst Studio APIs provide the endpoints needed to interact with Edgefirst Studio backend server.
                  For more details, please refer to the <a href="https://doc.edgefirst.ai/latest/perception/studio/" target="_blank" rel="noopener noreferrer">Edgefirst Help documentation</a>.
                </HelpTip>
              </div>

              <div>
                <label>
                  <input
                    v-model="manage_type"
                    type="radio"
                    value="cloud"
                    name="manage_group"
                    class="split-radio"
                  >
                  Cloud Managed
                </label>
              </div>

              <div>
                <label>
                  <input
                    v-model="manage_type"
                    type="radio"
                    value="local"
                    name="manage_group"
                    class="split-radio"
                  >
                  User Managed
                </label>
              </div>

              <div v-if="false">
                <label>
                  <input
                    v-model="manage_type"
                    type="radio"
                    value="kubernetes"
                    name="manage_group"
                    class="split-radio"
                  >
                  Kubernetes Managed
                </label>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-12 settings__control-group">
                <label class="required">Name</label>
                <input
                  v-model="name"
                  class="select"
                  type="text"
                >
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-12 settings__control-group">
                <label>Description (Optional)</label>
                <textarea
                  v-model="description"
                  rows="2"
                />
              </div>
            </div>
            <h2 class="mt-4">Training Data</h2>

            <SchemaDataset 
              :schemaField="schemaField"
              v-model:datasetId="datasetId"
              v-model:tagName="tagName"
              v-model:annotationSetId="annotationSetId"
              v-model:trainGroupName="trainGroupName"
              v-model:valGroupName="valGroupName"
              :enableAnnotationSetSelection="true"
              :enableGroupSelection="true"
           />

            <div class="row mt-3">
              <div class="col-12">
                <v-checkbox
                  v-model="use_default_weights"
                  color="#250e81"
                  :hide-details="true"
                  :density="'compact'"
                >
                  <template #label>
                    <span class="checkbox-label">Use Default Weights</span>
                  </template>
                </v-checkbox>
              </div>
            </div>
            <div v-if="!use_default_weights" class="row ms-2">
              <div class="col-12">
                <div class="row">
                  <div class="col-12 settings__control-group dataset-selector">
                    <label class="required">Experiment</label>
                    <select v-model="weightsExperiment" class="select" required>
                      <option disabled :value="''">Select an Experiment</option>
                      <option v-for="(exp, i) in experiments" :key="i" :value="exp">
                        {{ exp.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="row mt-2">
                  <div class="col-12 settings__control-group dataset-selector">
                    <label class="required">Training Session</label>
                    <select v-model="weightsTrainingSession" class="select" required>
                      <option disabled :value="''">Select a Training Session</option>
                      <option v-for="(ts, i) in weightsTrainingSessions" :key="i" :value="ts">
                        {{ ts.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!--     Dynamic Schema  -->

          <div class="dynamic-card">
            <h2>Model Settings</h2>
            <div class="container pt-2">
              <SchemaGroup
                :key="componentKey"
                v-model:params="configGroupValues"
                :fields="schema"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="footer">
        <button
          class="button modal-action-button"
          :class="{'button-spinner': startingTrainer}"
          :disabled="startingTrainer"
          @click="startSessionV2"
        >
          Start Session
        </button>
      </div>
    </template>
  </Modal>
  <ConfirmModal
    ref="sourceDatasetErrorModal"
    :messageHeader="sourceDatasetErrorHeader"
    :message="sourceDatasetErrorMessage"
    :note="sourceDatasetErrorNote"
  />
</template>

<script setup>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import Modal from '@/components/Modal.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import {
  inputSanitizer, changeSanitizer, encodeID,
} from '@/assets/js/utils';
import SchemaGroup from '@/components/Schema/SchemaGroup.vue';
import SchemaDataset from '@/components/Schema/SchemaDataset.vue';
import {
  ref, computed, watch, onMounted, onBeforeMount, nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import useTaskModal from '@/composables/useTaskModal.js';
import { event } from 'vue-gtag';
import HelpTip from '../../HelpTip.vue';

const props = defineProps({
  trainer: {
    type: Number,
    default: 0,
  },
  trainerSession: {
    type: Object,
    default: null,
  },
  experiments: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['create-trainer-session']);

const schemaField= {
  name: "Dataset",
}


const $toast = useToast();
const store = useStore();
const currentProject = computed(() => store.state.projects.currentProject);
const {
  pauseGetTasks,
  resumeGetTasks,
  isGettingTasks,
  getTasks,
} = useTaskModal();

// Refs
const createTrainerModal = ref(null);
const sourceDatasetErrorModal = ref(null);
const modalTitle = ref("Create Training Session");
const sourceDatasetErrorHeader = ref('');
const sourceDatasetErrorMessage = ref('');
const sourceDatasetErrorNote = ref('');
const schemaType = ref('modelpack');
const message = ref('');
const name = ref('');
const description = ref("");
const datasetId = ref('');
const tagName = ref('');
const annotationSetId = ref('');
const trainGroupName = ref('');
const valGroupName = ref('');
const trainGroup = ref('');
const valGroup = ref('');
const groupSplit = ref('percent');
const version_params = ref({
  version: '',
});
const custom_split_slider_values = ref([80, 20, 0]);
const custom_split_train_input = ref(0);
const custom_split_val_input = ref(0);
const dataset_split_params = ref({
  train: {
    d: '',
    set: '',
    group: '',
  },
  val: {
    d: '',
    set: '',
    group: '',
  },
  test: {
    d: '',
    set: '',
    group: '',
  },
});
const default_split_params = ref({
  train: {
    d: '',
    set: '',
  },
  val: {
    d: '',
    set: '',
  },
  test: {
    d: '',
    set: '',
  },
});
const modelList = ref([
  { name: 'ModelPack', value: 'modelpack' },
  { name: 'Legacy Detection', value: 'legacy' },
  { name: 'Legacy Segmentation', value: 'legacy' },
]);
const selectedModel = ref(null);
const startingTrainer = ref(false);
const advancedSettings = ref({
  upsample: 'resize',
});
const schema = ref(null);
const schemaList = ref([]);
const configGroupValues = ref({});
const componentKey = ref(0);
const manage_type = ref("cloud");
const use_default_weights = ref(true);
const weightsExperiment = ref('');
const weightsTrainingSessions = ref([]);
const weightsTrainingSession = ref('');
let dataConnect = null;

// Computed
const is_aws = computed(() => manage_type.value === "cloud");
const is_local = computed(() => manage_type.value === "local");
const is_kubernetes = computed(() => manage_type.value === "kubernetes");
const isDebugMode = computed(() => store.state.debugMode);
const datasetList = computed(() => store.state.datasets.datasetList);
const custom_split_params = computed(() => ({
  train: {
    percent: custom_split_slider_values.value[0],
    count: (custom_split_slider_values.value[0] / 100) * total.value,
  },
  val: {
    percent: custom_split_slider_values.value[1],
    count: (custom_split_slider_values.value[1] / 100) * total.value,
  },
  test: {
    percent: custom_split_slider_values.value[2],
    count: 0,
  },
}));
// Watchers
watch(() => props.trainerSession, (newVal) => {
  if (newVal == null) {
    modalTitle.value = "Create Training Session";
    return;
  }

  const sid = `v-${newVal.id.toString(16)}`;
  modalTitle.value = `Clone Training Session: Source ID: ${sid}`;

  //Copy Schema type
  schemaType.value = newVal.model;

  SetDatasetFromSourceTrainer(newVal);
  SetSchemaDefaultsFromSourceTrainer(newVal);
});

watch(configGroupValues, (value) => {
  // console.log(value);
}, { deep: true });

watch(schemaType, (v) => {
  schema.value = null;
  configGroupValues.value = {};
  getTrainerSchema(v);
});

watch(weightsExperiment, async (v) => {
  if (v) {
    weightsTrainingSessions.value = await getTrainingSessionList(v.id);
  }
});

// Lifecycle Hooks
onBeforeMount(() => {
  dataConnect = new DatastoreConnect(store.state.enterpriseServerUrl);
});

onMounted(() => {
  getAllTrainerSchema();
  getTrainerSchema(schemaType.value);
});

// Methods
async function getTrainerSchema(schemaTypeValue) {
  const params = {
    type: schemaTypeValue,
  };
  await dataConnect.getTrainerParametersSchema(params)
    .then((data) => {
      if (data.result) {
        schema.value = data.result;
      } else if (data.error) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: data.error.message,
          type: 'error',
          position: 'top',
        });
      }
      componentKey.value += 1;
    });
}

async function getAllTrainerSchema() {
  await dataConnect.getTrainerParametersSchema()
    .then((data) => {
      if (data.result) {
        schemaList.value = data.result?.schema_list;
      } else if (data.error) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: data.error.message,
          type: 'error',
          position: 'top',
        });
      }
      componentKey.value += 1;
    })
    .catch((error) => {
      console.log(error);
    });
}

function showModal() {
  createTrainerModal.value.showModal();
  if (props.trainerSession == null) {
    modalTitle.value = "Create Training Session";
    resetData();
  }
}

function resetData() {
  name.value = '';
  description.value = '';
  datasetId.value = '';
  tagName.value = '';
  annotationSetId.value = '';
  selectedModel.value = '';
  startingTrainer.value = false;
  groupSplit.value = 'percent';
  dataset_split_params.value = {
    train: {
      d: '',
      set: '',
      group: 0,
    },
    val: {
      d: '',
      set: '',
      group: 0,
    },
    test: {
      d: '',
      set: '',
      group: 0,
    },
  };
  default_split_params.value = {
    train: {
      d: '',
      set: '',
    },
    val: {
      d: '',
      set: '',
    },
    test: {
      d: '',
      set: '',
    },
  };
  version_params.value = {
    d: '',
    version: '',
  };
  use_default_weights.value = true;
  weightsExperiment.value = '';
  weightsTrainingSessions.value = [];
  weightsTrainingSession.value = '';
}

async function showMissingSourceDatasetError(sourceTrainerSession) {
  const sourceDatasetId = sourceTrainerSession?.params?.dataset_params?.dataset_id;
  const sourceDatasetUid = sourceDatasetId ? encodeID(sourceDatasetId, 'ds') : 'Unknown';
  // const sourceDatasetName = sourceTrainerSession?.params?.dataset_params?.dataset_name
  //   || sourceTrainerSession?.dataset_name
  //   || 'Unknown';

  sourceDatasetErrorHeader.value = 'Source Dataset Missing';
  sourceDatasetErrorMessage.value = 'The source dataset used by this training session no longer exists.';
  sourceDatasetErrorNote.value = `UID: ${sourceDatasetUid}`;

  createTrainerModal.value?.closeModal();
  await nextTick();
  sourceDatasetErrorModal.value?.showModal();
}

function SetDatasetFromSourceTrainer(s) {
  name.value = `${s.name}-COPY`;
  description.value = s.description;
  const sourceDatasetId = s?.params?.dataset_params?.dataset_id;
  const ds = sourceDatasetId
    ? datasetList.value.find((d) => d.id === sourceDatasetId)
    : null;

  if (!sourceDatasetId || !ds) {
    showMissingSourceDatasetError(s);
    return;
  }

  //Set Dataset
  datasetId.value = ds.id;
  //Set Annotation Set
  if (ds && s.params.dataset_params.annotation_set_id) {
    const as = ds.annotation_sets.find((a) => a.id === s.params.dataset_params.annotation_set_id);
    annotationSetId.value = as?.id || '';
  }
  // Set dataset Tag
  if (ds && s.params.dataset_params.tag_name) {
    tagName.value = s.params.dataset_params.tag_name;
  }
  
  selectedModel.value = '';
  startingTrainer.value = false;
  groupSplit.value = 'percent';
  dataset_split_params.value = {
    train: {
      d: '',
      set: '',
      group: 0,
    },
    val: {
      d: '',
      set: '',
      group: 0,
    },
    test: {
      d: '',
      set: '',
      group: 0,
    },
  };
  default_split_params.value = {
    train: {
      d: '',
      set: '',
    },
    val: {
      d: '',
      set: '',
    },
    test: {
      d: '',
      set: '',
    },
  };
  version_params.value = {
    d: '',
    version: '',
  };
  use_default_weights.value = true;
  weightsExperiment.value = '';
  weightsTrainingSessions.value = [];
  weightsTrainingSession.value = '';
}

function SetSchemaDefaultsFromSourceTrainer(s) {
  const params = s.params.model_params;
  traverse(params, "");
}

function traverse(obj, path = "") {
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      traverse(item, `${path}[${index}]`);
    });
  } else if (typeof obj === "object" && obj !== null) {
    Object.entries(obj).forEach(([key, value]) => {
      traverse(value, path ? `${path}.${key}` : key);
    });
  } else {
    // Set Default in schema
    SetSchemaDefaults(path, obj);
  }
}

function SetSchemaDefaults(path, value) {
  let obj = schema.value;

  const pf = path.split(".");
  for (let k = 0; k < pf.length; k++) {
    const p = pf[k];
    let index = -1;
    if (typeof obj.children !== "undefined") {
      index = obj.children.findIndex((item) => item.name === p);
      if (index < 0) return;
      obj = obj.children[index];
    } else {
      index = obj.findIndex((item) => item.name === p);
      if (index < 0) return;
      obj = obj[index];
    }
  }
  obj.default = value;
}

async function startSessionV2() {
  if (!name.value) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: 'Missing Name',
      type: 'error',
      position: 'top',
    });
    return;
  }

  const params = {
    trainer_id: props.trainer,
    split_mode: groupSplit.value,
    weights_session: weightsTrainingSession.value?.id,
    params: configGroupValues.value,
  };

  if (groupSplit.value === 'percent') {
    if (!datasetId.value) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Dataset',
        type: 'error',
        position: 'top',
      });
      return;
    }

    if (!tagName.value) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Please Select Dataset Tag',
        type: 'error',
        position: 'top',
      });
      return;
    }

    if (!annotationSetId.value) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Annotation Set',
        type: 'error',
        position: 'top',
      });
      return;
    }

    if (!trainGroupName.value) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Training Group',
        type: 'error',
        position: 'top',
      });
      return;
    }

    if (!valGroupName.value) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Validation Group',
        type: 'error',
        position: 'top',
      });
      return;
    }

    if (!use_default_weights.value) {
      if (!weightsExperiment.value) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Missing Weights Experiment',
          type: 'error',
          position: 'top',
        });
        return;
      }

      if (!weightsTrainingSession.value) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Missing Weights Training Session',
          type: 'error',
          position: 'top',
        });
        return;
      }
    }

    params['dataset_id'] = datasetId.value;
    params['tag_name'] = tagName.value;
    params['annotation_set_id'] = annotationSetId.value;
    params['train_group_name'] = trainGroupName.value;
    params['val_group_name'] = valGroupName.value;
    params['train_split'] = custom_split_slider_values.value[0];
    params['val_split'] = custom_split_slider_values.value[1];
    params['test_split'] = custom_split_slider_values.value[2];
  }
  if (groupSplit.value === 'group') {
    if (!dataset_split_params.value.train.d) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Train Dataset',
        type: 'error',
        position: 'top',
      });
      return;
    }
    params['train_dataset_id'] = dataset_split_params.value.train.d.id;

    if (!dataset_split_params.value.train.set) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Train Annotation Set',
        type: 'error',
        position: 'top',
      });
      return;
    }
    params['train_set_id'] = dataset_split_params.value.train.set.id;

    if (dataset_split_params.value.train.group <= 0) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Train Group',
        type: 'error',
        position: 'top',
      });
      return;
    }
    params['train_group'] = dataset_split_params.value.train.group;

    if (!dataset_split_params.value.val.d) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Val Dataset',
        type: 'error',
        position: 'top',
      });
      return;
    }
    params['val_dataset_id'] = dataset_split_params.value.val.d.id;

    if (!dataset_split_params.value.val.set) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Val Annotation Set',
        type: 'error',
        position: 'top',
      });
      return;
    }
    params['val_set_id'] = dataset_split_params.value.val.set.id;

    if (dataset_split_params.value.val.group <= 0) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Val Group',
        type: 'error',
        position: 'top',
      });
      return;
    }
    params['val_group'] = dataset_split_params.value.val.group;

    if (dataset_split_params.value.test.set && dataset_split_params.value.test.group > 0) {
      params['test_dataset_id'] = dataset_split_params.value.test.d.id;
      params['test_set_id'] = dataset_split_params.value.test.set.id;
      params['test_group'] = dataset_split_params.value.test.group;
    }
  }
  if (groupSplit.value === 'version') {
    if (!datasetId.value) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Dataset',
        type: 'error',
        position: 'top',
      });
      return;
    }

    if (!version_params.value.v) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Version',
        type: 'error',
        position: 'top',
      });
      return;
    }
    params['dataset_id'] = datasetId.value;
    params['version'] = version_params.value.v;
  }
  if (groupSplit.value === 'default') {
    if (!default_split_params.value.train.d) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Train Dataset',
        type: 'error',
        position: 'top',
      });
      return;
    }
    params['train_dataset_id'] = default_split_params.value.train.d.id;

    if (!default_split_params.value.train.set) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Train Annotation Set',
        type: 'error',
        position: 'top',
      });
      return;
    }
    params['train_set_id'] = default_split_params.value.train.set.id;

    if (!default_split_params.value.val.d) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Val Dataset',
        type: 'error',
        position: 'top',
      });
      return;
    }
    params['val_dataset_id'] = default_split_params.value.val.d.id;

    if (!default_split_params.value.val.set) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Missing Val Annotation Set',
        type: 'error',
        position: 'top',
      });
      return;
    }
    params['val_set_id'] = default_split_params.value.val.set.id;

    if (default_split_params.value.test.set) {
      params['test_dataset_id'] = default_split_params.value.test.d.id;
      params['test_set_id'] = default_split_params.value.test.set.id;
    }
  }

  if (name.value) {
    params.session_name = name.value;
  }
  if (description.value) {
    params.session_description = description.value;
  }
  if (schemaType.value) {
    params.trainer_type = schemaType.value;
  }

  startingTrainer.value = true;
  await dataConnect.startCloudInstance({
    params,
    type: 'trainer',
    name: params.session_name,
    project_id: currentProject.value.id,
    is_cloud: is_aws.value,
    is_local: is_local.value,
    is_kubernetes: is_kubernetes.value,
  })
    .then((data) => {
      if (data.result) {
        event('create_training_session_success', {
          category: 'create_training_session',
          label: 'User successfully created a training session',
          user: store.state.user.user.username,
        });
        getTasks();
        if (!isGettingTasks.value) {
          resumeGetTasks();
        }
        createTrainerModal.value.closeModal();
        emit('create-trainer-session');
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Train session in progress.`,
          type: 'success',
          position: 'top',
        });
      } else if (data.error) {
        event('create_training_session_failure', {
          category: 'create_training_session',
          label: 'User failed to create a training session',
          user: store.state.user.user.username,
          reason: data.error.message,
        });
        $toast.open({
          queue: true,
          duration: 5000,
          message: data.error.message,
          type: 'error',
          position: 'top',
        });
      }
    })
    .catch((error) => {
      console.log(error);
    });
  startingTrainer.value = false;
}

function getSessionName(session) {
  if (session.name) {
    return session.name;
  } else if (session.docker_task) {
    return session.docker_task.date;
  }
  return 'Unknown Session';
}

async function getTrainingSessionList(trainer_id) {
  const resp = await dataConnect.getTrainingSessionList({ trainer_id, reverse: true })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve training sessions: ${error}`,
        type: "error",
        position: "top",
      });
    });
  if (!resp || resp.error || !resp.result) {
    $toast.open({
      queue: true,
      dismissible: true,
      message: `Failed to retrieve training sessions: ${resp.error.message}`,
      type: "error",
      position: "top",
    });
    return [];
  }

  return resp.result;
}

defineExpose({
  showModal,
});</script>

<style lang="scss" scoped>
// $cardMargin: 8px;
$cardPadding: 16px;

.settings {
  padding-top: 16px;
  padding-bottom: 16px;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  background: var(--color-white-600);

  span {
    width: 100%;
    font-size: 1rem;
    font-weight: 700;
    text-align: left;
    @include themify() {
      color: themed('color-primary');
    }
  }
}
.container {
  display: flex;
  flex-direction: row;
  padding: 0 16px;
  gap: 16px;
}

h2 {
    color: #250E81;
  }

.static-card {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: $cardPadding;
  text-align: left;
  border-radius: 6px;
  height: fit-content;
  flex: 1 1 auto;
  border: 1px solid var(--color-primary-200);
  // margin: $cardMargin;
  width: 600px;
  background: var(--color-white-100);
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
}

.dynamic-card {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: $cardPadding;
  text-align: left;
  border-radius: 6px;
  height: fit-content;
  flex: 1 1 auto;
  border: 1px solid var(--color-primary-200);
  // margin: $cardMargin;
  width: 600px;
  background: var(--color-white-100);
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
}

:deep(.dynamic-card > .container > .model-settings > .field-group:not(:first-of-type)) {
  padding-top: 24px;
}

@media (max-width: $breakpoint-lg) {
  .settings {
    width: 100%;
  }

  .container {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  // .static-card {
  //   width: calc(100% - (2 * #{$cardMargin}));
  // }

  // .dynamic-card {
  //   width: calc(100% - (2 * #{$cardMargin}));
  // }
}

.footer {
  display: flex;
  flex-direction: column;
  padding: 0.75rem 3rem;
  justify-content: flex-end;
  border-top: solid 3px var(--gray-300);
  background: var(--color-white-100);

  button {
    margin-left: auto;
  }
}

.result {
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  font-size: 1rem;
  font-weight:600;
  padding: 10px;
  align-items: center;
  justify-content: center;

  span {
    width: 100%;
    line-break: anywhere;
  }
}

.error {
  @include themify() {
    background: rgba(182, 94, 94, 0.5) ;
    color: themed('color-error') !important;
  }
}

.radio-group {
  display: inline-block;
  margin: 20px;
  border-radius: 5px;
  overflow: hidden;
  @include themify() {
    border: solid 1px themed('color-primary');
  }
}

.radio-group:focus-within, .radio-group:active {
  @include themify() {
    box-shadow:0 0 5px themed('color-primary');
  }
}

.radio-group input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.radio-group input[type="radio"] + label {
  min-width: 6rem;
  max-width: 50%;
  text-align: center;
  display: inline-block;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  line-height: 1.25;
  padding: 5px 20px;
  transition: all 0.4s ease;
  box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.25) inset;
  @include themify() {
    color: themed('color-primary');
    background: themed('color-white-100');
  }
}

.radio-group input[type="radio"]:not(:disabled):hover + label {
  @include themify() {
    background: themed('color-primary-200');
    color: themed('primary-text-color');
  }
}

.radio-group input[type="radio"]:checked + label,
.radio-group input[type="radio"]:checked:hover + label {
  opacity: 1;
  z-index: 1;
  box-shadow: 0 0 6px -2px rgba(0, 0, 0, 0.25), 0 0 6px 2px rgba(0, 0, 0, 0.25);
  @include themify() {
    background: themed('color-primary');
    color: themed('primary-text-color');
  }
}

.radio-group.full-width-selector {
  display: flex;
  width: 100%;
  margin: 0;
}

.radio-group.full-width-selector input[type="radio"] + label {
  min-width: 1px;
  padding: 3px 10px;
  flex: 1 1 auto;
}

.dataset-selector{
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: auto;

  :first-child {
    flex: 1 1 40%;
  }

  :not(:first-child) {
    flex: 1 1 60%;
  }
}

.trainer-session-row-switch {
  flex-direction: row !important;
  overflow: visible;
  align-items: center;

  label:first-child {
    flex: 1 1 40%;
    max-width: 40%;
  }
}

.full-width {
  width: 100%;
}

.custom-split-card {
  border-radius: 6px;
  padding: 10px;

  p {
    font-size: 0.9rem;
    font-weight: 700;
  }
}

.custom-split-card + .custom-split-card {
  margin-left: 15px;
}

.slider-settings {
  display: flex;
  flex-direction: row;
  padding: 12px;
  border-radius: inherit;
  gap: 6px;
  min-width: 350px;
  min-height: 50px;
  justify-content: center;
  align-items: center;
  overflow: auto;

  &__row {
    display: flex;
    align-items: center;
  }

  & input[type=checkbox] {
    margin-right: 12px;
  }

  & span {
    width: 40px;
    text-align: center;
  }
}

._train{
    border: 2px solid rgb(44, 145, 246);
}

._validation{
  @include themify {
    border: 2px solid themed('range-slider-selected-color');
  }
}

._test{
  @include themify {
    border: 2px solid themed('range-slider-selected-color-right');
  }
}

.clickable {
  cursor: pointer;
}

.split-radio[type="radio"] {
  height: 18px;
  width: 18px;
  @include themify() {
    accent-color: themed('color-primary');
  }
}

:deep() {
  .v-label{
    opacity: 1 !important;
  }
}

.checkbox-label {
  margin-left: 5px;
  font-size: 14px !important;
  font-weight: 600!important;
  letter-spacing: 0 !important;
  @include themify() {
    color: themed('form-label-color') !important;
  }
}

</style>
